import { api } from "../lib";
import type { StagedBlockDraft } from "./stagedBlockRequest";

// ==================== TYPES ====================

export interface RequestAdditionalStainDto {
  specimenId: string;
  blockId: string;
  stainId: string;
  providerId?: string | null;
  levels?: "none" | "x3" | null;
  remarks?: string;
  priority?: number;
}

export interface PendingStainInfo {
  id: string;
  slideNumber: string;
  stain: {
    id: string;
    code: string;
    description: string;
    stainType: string;
  };
  block: {
    id: string;
    number: string;
  };
  specimen: {
    id: string;
    number: string;
    source?: string;
  };
  provider: {
    id: string | null;
    name: string;
    isInHouse: boolean;
  };
  status: string;
  levelsRequested: string | null;
  remarks: string | null;
  requestedAt: string;
  requestedBy: {
    id: string;
    name: string;
  };
  estimatedReturnDate?: string | null;
  // Set if this stain was auto-ordered via the macro-to-stain linking system
  autoLinkedDiagnosisId?: string | null;
  // Present when this occurrence belongs to a locked panel order.
  panelOrderComponentId?: string | null;
  // Child case info for workflow tracking
  childCase?: {
    id: string;
    caseNumber: string;
    status: string;
  };
}

// Response from requesting an additional stain
// Returns child case for in-house stains OR sendOut for external stains
export interface AdditionalStainRequestResult {
  childCase: {
    id: string;
    caseNumber: string;
    status: string;
  } | null;
  sendOut: {
    stainCode: string;
    providerName: string;
  } | null;
  slide: {
    id: string;
    slideNumber: string;
    stainCode: string;
  };
  parentCaseId: string;
}

export interface StainSearchResult {
  id: string;
  code: string;
  description: string;
  stainType: string;
  isAvailableInHouse: boolean;
  defaultProvider: {
    id: string;
    name: string;
  } | null;
  availableProviders: Array<{
    id: string;
    name: string;
    turnaroundDays: number | null;
    cost: number | null;
    isPreferred: boolean;
  }>;
}

export interface StainProvider {
  id: string | null;
  name: string;
  isInHouse: boolean;
  turnaroundDays: number | null;
  cost: number | null;
  isPreferred: boolean;
}

// ==================== SERVICE FUNCTIONS ====================

/**
 * Request an additional stain during pathologist review
 * Returns the created child case info for in-house stains
 */
export function requestAdditionalStain(
  caseId: string,
  dto: RequestAdditionalStainDto
): Promise<AdditionalStainRequestResult> {
  return api.post<AdditionalStainRequestResult>(
    `/workflows/pathologist-review/${caseId}/request-stain`,
    dto
  );
}

/**
 * Get pending stain requests for a case
 */
export function getPendingStains(caseId: string): Promise<PendingStainInfo[]> {
  return api.get<PendingStainInfo[]>(
    `/workflows/pathologist-review/${caseId}/pending-stains`
  );
}

/**
 * Search stains for the request modal with provider info
 */
export function searchStainsForRequest(
  query: string,
  limit = 10
): Promise<StainSearchResult[]> {
  return api.get<StainSearchResult[]>("/workflows/stains/search", {
    params: { q: query, limit },
  });
}

/**
 * Get providers available for a specific stain
 */
export function getProvidersForStain(
  stainId: string
): Promise<StainProvider[]> {
  return api.get<StainProvider[]>(`/workflows/stains/${stainId}/providers`);
}

export interface DeleteStainResult {
  slideId: string;
  caseId: string;
  expiredStainerOrders: number;
  cancelledSendOutId: string | null;
  childCaseDeleted: boolean;
}

/**
 * Remove a stain slide from a case. The backend refuses (409) once the case is
 * signed off or submitted to billing, and records every deletion in the audit log.
 */
export function deleteStain(
  slideId: string,
  reason?: string | null
): Promise<DeleteStainResult> {
  return api.delete<DeleteStainResult>(`/workflows/slides/${slideId}`, {
    data: reason ? { reason } : undefined,
  });
}

// ==================== BATCH TYPES ====================

export interface StainRequestBatchItem {
  specimenId: string;
  blockId?: string;
  stagedBlockKey?: string;
  stainId: string;
  providerId?: string | null;
  levels?: "none" | "x3" | null;
  // Display-only fields (not sent to backend)
  stainCode: string;
  stainDescription: string;
  specimenLabel: string;
  blockLabel: string;
}

export interface RequestAdditionalStainsBatchDto {
  items: Array<{
    specimenId: string;
    blockId?: string;
    stagedBlockKey?: string;
    stainId: string;
    providerId?: string | null;
    levels?: "none" | "x3" | null;
  }>;
  stagedBlocks?: StagedBlockDraft[];
  remarks?: string;
}

export interface AdditionalStainBatchResult {
  childCase: {
    id: string;
    caseNumber: string;
    status: string;
  } | null;
  sendOuts: Array<{
    stainCode: string;
    providerName: string;
  }>;
  slides: Array<{
    id: string;
    slideNumber: string;
    stainCode: string;
  }>;
  parentCaseId: string;
}

export type AdditionalStainRequestSurface =
  | "pathologist-review"
  | "case-details";

// ==================== BATCH SERVICE FUNCTION ====================

/**
 * Request a batch of additional stains during pathologist review.
 * Creates one child case with multiple slides.
 */
export function requestAdditionalStainsBatch(
  caseId: string,
  dto: RequestAdditionalStainsBatchDto,
  surface: AdditionalStainRequestSurface = "pathologist-review"
): Promise<AdditionalStainBatchResult> {
  return api.post<AdditionalStainBatchResult>(
    `/workflows/${surface}/${caseId}/request-stains-batch`,
    dto
  );
}

// ==================== SLIDE DONE TOGGLE ====================

export interface ToggleSlideDoneResult {
  id: string;
  isCompleted: boolean;
  completedAt: string | null;
  completedByName: string | null;
}

/**
 * Toggle the Done (isCompleted) flag on a slide in pathologist review
 */
export function toggleSlideDone(
  slideId: string
): Promise<ToggleSlideDoneResult> {
  return api.patch<ToggleSlideDoneResult>(
    `/workflows/slides/${slideId}/toggle-done`
  );
}

export interface SpecialStainInfo {
  id: string;
  code: string;
  description: string;
  cptCode: {
    code: string;
    description: string;
  } | null;
}

/**
 * Fetch all active stains available for stain/CPT correction
 */
export function getSpecialStains(): Promise<SpecialStainInfo[]> {
  return api.get<SpecialStainInfo[]>("/workflows/special-stains");
}

/**
 * Correct slide CPT/stain association in-place
 */
export function correctSlideCpt(
  slideId: string,
  data: { newStainId: string; reason?: string }
): Promise<any> {
  return api.patch<any>(`/workflows/slides/${slideId}/correct-cpt`, data);
}

// Export as namespace for convenience
export const stainRequestService = {
  requestAdditionalStain,
  requestAdditionalStainsBatch,
  getPendingStains,
  searchStainsForRequest,
  getProvidersForStain,
  deleteStain,
  toggleSlideDone,
  getSpecialStains,
  correctSlideCpt,
};
